import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { useLessonPlayerStore } from '@stores/lessonPlayerStore';
import { CONTROLS_PADDING_X } from '@constants/unacademyIcons/icons.constants';
import { PLAYER_Z_INDEX } from '@unicons-constants/style.constants';
import SecondaryControls from './SecondaryControls';
import PrimaryControls from './PrimaryControls';

interface ControlBarProps {
  isTrailer?: boolean;
}

type ControlBarWrapperProps = {
  $visible: boolean;
};

type ButtonsWrapper = {
  $isTimeWrapper?: boolean;
  $isMobileDevice?: boolean;
  $isFullscreen?: boolean;
  $height?: string;
  $hasPadding?: boolean;
};

const ControlBarWrapper = styled.div<ControlBarWrapperProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: ${({ $visible }) =>
    $visible
      ? 'opacity 100ms ease-out 0s'
      : 'opacity 1000ms ease-out 0s'} !important;
  position: absolute;
  width: 100%;
  background-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.4) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  padding: ${Spacings.SPACING_3B} ${CONTROLS_PADDING_X}px 0;

  bottom: 0;
  z-index: ${PLAYER_Z_INDEX.CONTROLS_WRAPPER};
  ${mediaQuery.uptoTablet} {
    z-index: ${PLAYER_Z_INDEX.WRAPPER};
    padding: 0px ${CONTROLS_PADDING_X}px;
  }
`;

const ControlButtonsWrapper = styled.div<ButtonsWrapper>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: ${PLAYER_Z_INDEX.BUTTONS_WRAPPER};
  flex-grow: ${({ $isTimeWrapper, $isMobileDevice }) =>
    $isTimeWrapper && $isMobileDevice ? 1 : 0};
  height: ${({ $height }) => $height};
  padding: ${({ $hasPadding, $isFullscreen }) =>
    $hasPadding ? `${$isFullscreen ? '16px 0 32px' : '16px 0 20px'}` : '0px'};
  ${mediaQuery.uptoMobile} {
    padding-top: 2px;
    padding-bottom: 2px;
  }
`;

const ControlBar: React.FC<ControlBarProps> = ({ isTrailer }) => {
  const {
    isControlsVisible,
    isXtraVisible,
    isFullscreen,
    isNextLessonVisible,
    isNextLessonCancelled
  } = useLessonPlayerStore();

  const isVisible = useMemo(
    () =>
      isControlsVisible &&
      !isXtraVisible &&
      (!isNextLessonVisible || isNextLessonCancelled),
    [
      isControlsVisible,
      isXtraVisible,
      isNextLessonVisible,
      isNextLessonCancelled
    ]
  );

  return (
    <ControlBarWrapper $visible={isVisible}>
      <ControlButtonsWrapper
        $height="100%"
        $isFullscreen={isFullscreen && !isTrailer}
        $hasPadding
      >
        <ControlButtonsWrapper>
          <PrimaryControls isMobileControls={false} isMobileDevice={false} />
        </ControlButtonsWrapper>

        <ControlButtonsWrapper $isTimeWrapper $isMobileDevice={false}>
          <SecondaryControls />
        </ControlButtonsWrapper>
      </ControlButtonsWrapper>
    </ControlBarWrapper>
  );
};

ControlBar.displayName = 'ControlBar';

export default ControlBar;
